import React, { useState, useEffect } from 'react';
import { useCommodityData } from './useCommodityData';
import { COMMODITIES, SVG_ICONS } from './config';
import { fmtTime } from './utils';
import CommodityCard from './CommodityCard';
import ChartModal from './ChartModal';
import './CommodityTracker.css';

export default function CommodityTrackerView() {
  const { data, forceRefresh } = useCommodityData(5000);
  const [activeCategory, setActiveCategory] = useState('all');
  const [theme, setTheme] = useState('dark');
  const [chartKey, setChartKey] = useState(null);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('commodity-theme') || 'dark';
    setTheme(savedTheme);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const p1 = forceRefresh(true);
    const p2 = new Promise(resolve => setTimeout(resolve, 600));
    await Promise.all([p1, p2]);
    setIsRefreshing(false);
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('commodity-theme', next);
  };
  console.log(data);
  const hasAnyData = Object.keys(data.prices).length > 0;
  const errCount = Object.keys(data.errors).length;
  let statusClass = 'status-pill err';
  let statusText = 'OFFLINE';

  if (hasAnyData && errCount === 0) {
    statusClass = 'status-pill';
    statusText = 'LIVE';
  } else if (hasAnyData) {
    statusClass = 'status-pill err';
    statusText = `PARTIAL (${errCount} ERR)`;
  } else if (data.isLoading) {
    statusClass = 'status-pill err';
    statusText = 'LOADING';
  }

  const keysToRender = Object.keys(COMMODITIES).filter(key => {
    if (activeCategory === 'all') return true;
    return COMMODITIES[key].category === activeCategory;
  });

  return (
    <div className={`commodity-tracker-wrapper ${theme}`} data-theme={theme}>
      <div className="ambient-orb ambient-orb-1" />
      <div className="ambient-orb ambient-orb-2" />

      <div className="wrap relative z-10">
        {/* HEADER */}
        <header>
          <div className="header-left">
            <div className="header-icon" dangerouslySetInnerHTML={{ __html: SVG_ICONS.chart }} />
            <div>
              <h1 className="h-title">Commodity Price Tracker</h1>
              <p className="h-sub">Live Prices · India Import Landed · Customs Duty</p>
            </div>
            <div className="logo-glow" />
          </div>
          <div className="h-right">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <div className={statusClass}>
                <div className="status-dot" />
                <span>{statusText}</span>
              </div>
              <button className="hdr-btn flex items-center gap-1" onClick={toggleTheme}>
                <span style={{ display: 'inline-flex', width: '14px', height: '14px' }} dangerouslySetInnerHTML={{ __html: theme === 'dark' ? SVG_ICONS.sun : SVG_ICONS.moon }} />
                <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
              </button>
              <button className="hdr-btn flex items-center gap-1" onClick={handleRefresh} disabled={isRefreshing}>
                <span style={{ display: 'inline-flex', width: '14px', height: '14px' }} dangerouslySetInnerHTML={{ __html: SVG_ICONS.refresh }} />
                <span>{isRefreshing ? 'Loading...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </header>

        {/* CATEGORY TABS */}
        <div className="category-tabs">
          {[
            { id: 'all', label: 'All', icon: SVG_ICONS.globe },
            { id: 'precious', label: 'Precious Metals', icon: SVG_ICONS.sparkle },
            { id: 'industrial', label: 'Industrial Metals', icon: SVG_ICONS.wrench },
            { id: 'energy', label: 'Energy', icon: SVG_ICONS.bolt }
          ].map(cat => (
            <button
              key={cat.id}
              className={`cat-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span dangerouslySetInnerHTML={{ __html: cat.icon }} /> {cat.label}
            </button>
          ))}
        </div>

        {/* COMMODITY CARDS GRID */}
        <div className="commodity-grid">
          {keysToRender.map(key => (
            <CommodityCard
              key={key}
              commodityKey={key}
              priceData={data.prices[key]}
              usdInr={data.usdInr}
              onOpenChart={setChartKey}
              tickTime={data.lastUpdate ? fmtTime(data.lastUpdate) : '—'}
              lastUpdate={data.lastUpdate}
            />
          ))}
        </div>

        {/* METHODOLOGY */}
        <div className="card methodology-card" style={{ marginTop: '32px' }}>
          <details open={methodologyOpen} onToggle={(e) => setMethodologyOpen(e.target.open)}>
            <summary className="methodology-summary flex items-center justify-between cursor-pointer focus:outline-none">
              <span className="c-title" style={{ fontSize: '14px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span dangerouslySetInnerHTML={{ __html: SVG_ICONS.ruler }} style={{ width: '16px', height: '16px' }} />
                Methodology
              </span>
              <span className="methodology-arrow">▸</span>
            </summary>
            {methodologyOpen && (
              <div className="methodology-content mt-4">
                <div className="methodology-grid grid md:grid-cols-2 gap-4">
                  <div className="inner-panel p-4 rounded-lg">
                    <h3 className="text-[13px] font-bold text-[var(--t1)] mb-2">Core Formula</h3>
                    <code className="formula-code block p-3 rounded-md bg-[var(--primary)] bg-opacity-10 border border-[var(--primary)] border-opacity-20 text-[var(--primary)] text-xs font-mono">
                      India Landed Price = (Intl. Price ÷ Unit) × USD/INR × (1 + Duty%)
                    </code>
                  </div>
                  <div className="inner-panel p-4 rounded-lg">
                    <h3 className="text-[13px] font-bold text-[var(--t1)] mb-2">Gold & Silver</h3>
                    <p className="methodology-text text-xs text-[var(--t3)]">Spot price via XAU/USD & XAG/USD → Convert troy oz to grams (÷ 31.1035) → Multiply by USD/INR → Apply 6% duty (5% BCD + 1% AIDC).</p>
                    <p className="methodology-text text-xs text-[var(--t3)] mt-1"><strong>Purity:</strong> 22K = 24K × (22/24), 18K = 24K × (18/24)</p>
                  </div>
                  <div className="inner-panel p-4 rounded-lg">
                    <h3 className="text-[13px] font-bold text-[var(--t1)] mb-2">Base Metals</h3>
                    <p className="methodology-text text-xs text-[var(--t3)]">LME prices (USD/MT) via metals.live → Convert to ₹/kg (÷ 1000 × USD/INR) → Apply respective duty rates. Copper: 2.5%, Aluminium: 7.5%, Zinc: 5%, Lead: 5%, Nickel: 0%.</p>
                  </div>
                  <div className="inner-panel p-4 rounded-lg">
                    <h3 className="text-[13px] font-bold text-[var(--t1)] mb-2">Energy & Platinum</h3>
                    <p className="methodology-text text-xs text-[var(--t3)]">Crude Oil (WTI USD/barrel) → Apply ~5% duty. Natural Gas (USD/MMBtu) → Apply 2.5% duty. Platinum → Apply 15.39% duty (12.5% BCD + Social Welfare Surcharge).</p>
                  </div>
                </div>
                <div className="methodology-footer mt-4 pt-4 border-t border-[var(--sep2)]">
                  <p className="text-[10px] text-[var(--t4)]">Data sources: Yahoo Finance (international futures), Metals.live (LME metals). Prices are calculated mathematically and are for illustrative purposes only.</p>
                  <p className="mt-2 text-[10px] text-[var(--t2)] font-semibold">IMPORTANT: This application does NOT display live data from NSE or MCX. All Indian quotes (including Standard Contract Equivalents) are mathematical approximations derived from international benchmarks converted to INR via live forex rates.</p>
                </div>
              </div>
            )}
          </details>
        </div>

        {/* CHART MODAL */}
        {chartKey && <ChartModal chartKey={chartKey} onClose={() => setChartKey(null)} theme={theme} />}

      </div>
    </div>
  );
}
